
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Calendar, DollarSign, MapPin, User, Eye } from "lucide-react";
import { TourismPackage } from "./types";
import { PackageEditDialog } from "./PackageEditDialog";

interface PackageGridProps {
  packages: TourismPackage[];
  onDelete: (packageId: string) => void;
}

export const PackageGrid = ({ packages, onDelete }: PackageGridProps) => {
  const [editingPackage, setEditingPackage] = useState<TourismPackage | undefined>();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'unpaid': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEdit = (pkg: TourismPackage) => {
    setEditingPackage(pkg);
    setIsEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    // يمكن إضافة refresh للبيانات هنا
    window.location.reload();
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg) => (
          <Card key={pkg.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-lg mb-2">{pkg.title}</CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {pkg.destination}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(pkg)}
                    className="p-2"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onDelete(pkg.id)}
                    className="p-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm">{pkg.duration}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-gray-500" />
                <span className="text-lg font-semibold">{pkg.price?.toLocaleString()} ر.س</span>
              </div>

              {pkg.client_name && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">{pkg.client_name}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center pt-2">
                <Badge className={getStatusColor(pkg.status)}>
                  {pkg.status === 'active' ? 'نشط' : pkg.status === 'draft' ? 'مسودة' : 'منتهي'}
                </Badge>
                <Badge className={getPaymentStatusColor(pkg.payment_status)}>
                  {pkg.payment_status === 'paid' ? 'مدفوع' : pkg.payment_status === 'pending' ? 'قيد الانتظار' : 'غير مدفوع'}
                </Badge>
              </div>
              
              <div className="text-xs text-gray-500 pt-2">
                تم الإنشاء: {new Date(pkg.created_at).toLocaleDateString('ar-SA')}
              </div>

              <div className="flex gap-2 mt-4">
                <Link to={`/package-details/${pkg.id}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full">
                    <Eye className="w-4 h-4 ml-1" />
                    التفاصيل
                  </Button>
                </Link>
                <Button variant="outline" size="sm" onClick={() => handleEdit(pkg)}>
                  <Edit className="w-4 h-4 ml-1" />
                  تعديل
                </Button>
                <Button variant="destructive" size="sm" onClick={() => {
                  if (confirm('هل أنت متأكد من حذف هذه الباقة؟')) {
                    onDelete(pkg.id);
                  }
                }}>
                  <Trash2 className="w-4 h-4 ml-1" />
                  حذف
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <PackageEditDialog
        package={editingPackage}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={handleEditSuccess}
      />
    </>
  );
};
